import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, ReplaySubject, takeUntil } from 'rxjs';

import { Loggable } from 'app/utils/logging/loggable';
import {
  IncomingMessage,
  IncomingMessageType,
  OutgoingMessageType,
  AddFavoriteMessage,
  RemoveFavoriteMessage,
} from 'messages/websocket.messages';

import { WebsocketService } from './websocket.service';

export interface FavoriteResponse {
  userId: string;
  add: boolean;
  error: boolean;
  msg: string;
}

export const FAVORITE_INITIAL_STATE: FavoriteResponse = {
  userId: '',
  add: false,
  error: false,
  msg: '',
};

@Injectable({
  providedIn: 'root',
})
export class FavoriteService extends Loggable {
  private websocketService = inject(WebsocketService);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private favoriteResultStreamSource = new BehaviorSubject<FavoriteResponse>(FAVORITE_INITIAL_STATE);
  public favoriteResultStream$ = this.favoriteResultStreamSource.asObservable().pipe(distinctUntilChanged());

  public favoriteListStreamSource = new BehaviorSubject<string[]>([]);
  public favoriteListStream$ = this.favoriteListStreamSource.asObservable();

  private favoriteCache = new Map<string, boolean>();

  public constructor() {
    super();
    this.setDisplayName('FavoriteService');

    this.websocketService.message$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleWebsocketMessage.bind(this));
  }

  public getFavoriteList(): string[] {
    return this.favoriteListStreamSource.getValue();
  }

  public isFavorite(userId: string): boolean {
    if (this.favoriteCache.has(userId)) {
      return this.favoriteCache.get(userId)!;
    }

    const isFavorite = this.getFavoriteList().includes(userId);
    this.favoriteCache.set(userId, isFavorite);
    return isFavorite;
  }

  public updateFavorites(favoriteResponse: FavoriteResponse): void {
    if (!favoriteResponse.error) {
      const newFavorites = this.getFavoriteList();

      if (favoriteResponse.add) {
        this.favoriteCache.set(favoriteResponse.userId, true);
        if (!newFavorites.includes(favoriteResponse.userId)) {
          this.setFavoriteList([...newFavorites, favoriteResponse.userId]);
        }
      } else {
        this.favoriteCache.set(favoriteResponse.userId, false);
        this.setFavoriteList(newFavorites.filter((id) => id !== favoriteResponse.userId));
      }
    }
  }

  public setFavoriteList(favoriteList: string[]): void {
    this.favoriteCache.clear();
    this.favoriteListStreamSource.next(favoriteList);
  }

  private handleWebsocketMessage = (msg: IncomingMessage): void => {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.FAVORITE_RESULT: {
        this.favoriteResultStreamSource.next(msg);
        this.updateFavorites(msg);
        return;
      }
    }
  };

  // Request to favorite participants
  public favoriteParticipant(actualUserId?: string, toFavoriteUserId?: string) {
    if (toFavoriteUserId && actualUserId !== toFavoriteUserId) {
      try {
        const msg: AddFavoriteMessage = {
          t: OutgoingMessageType.ADD_FAVORITE,
          userId: toFavoriteUserId,
        };
        this.websocketService.sendMessage(msg);
      } catch (e) {
        this.error('Failed to add user to favorite', e);
      }
    }
  }

  // Request to unfavorite participants
  public unfavoriteParticipant(actualUserId?: string, toUnfavoriteUserId?: string) {
    if (toUnfavoriteUserId && actualUserId !== toUnfavoriteUserId) {
      try {
        const msg: RemoveFavoriteMessage = {
          t: OutgoingMessageType.REMOVE_FAVORITE,
          userId: toUnfavoriteUserId,
        };
        this.websocketService.sendMessage(msg);
      } catch (e) {
        this.error('Failed to add user to favorite', e);
      }
    }
  }

  public destroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
